import React, { useEffect, useState, useMemo, useRef } from 'react';
import * as authSelectors from 'redux/selectors/auth';
import * as classSelectors from 'redux/selectors/class';
import { actions as classActions } from 'redux/modules/class';
import { actions as courseActions } from 'redux/modules/course';
import { connect } from 'react-redux';
import MainLayout from 'components/MainLayout';
import {
  Button,
  Container,
  Header,
  HeaderActions,
  IconButton,
  PanelArea,
  Title,
} from './styles';
import times from 'img/times.svg';
import arrowPrev from 'img/arrowPrev.svg';
import Loading from 'components/Loading';
import { Dispatch } from 'redux';
import { Instructions } from './components/Instructions';
import playWhite from 'img/play-white.svg';
import stopWhite from 'img/stop-white.svg';
import infomationIcon from 'img/information-icon.svg';
import trophyIcon from 'img/trophy-finish-lecture.svg';
import { ModalInformation, ModalInformationProps } from 'pages/course/components/ModalInformation';
import { QuestionRenderer } from './components/QuestionRenderer';
import { LecturesHeaderPanel } from './components/LecturesHeaderPanel';
import { LecturesFooterPanel } from './components/LecturesFooterPanel';
import {
  LectureData,
  LectureQuestion,
  LectureAnswersState,
  isQuestionAnswered,
  isQuestionBlocksMode,
  QuestionBlockSummary,
  LectureBlockQuestionsResponse,
  ReviewResponse,
  ReviewQuestion,
} from './types';
import { usePersistLectureAnswers } from './hooks/usePersistLectureAnswers';
import { useRestoreLectureAnswers } from './hooks/useRestoreLectureAnswers';
import {
  formatTime,
  itemsSelectOnReview,
  QUESTIONS_SELECT_AREA_ID,
  QUESTIONS_SELECT_SCROLL_STEP,
  buildFinishLecturePayload,
  scopeQuestionsToReview,
  shouldExitToBlocksList,
  BlockOrigin,
} from './utils';
import {
  finishLecture,
  getLectureWrongQuestions,
  getLectureDetails as fetchLectureDetailsApi,
  getLectureBlockQuestions,
} from 'redux/api/class';
import { QuestionBlocksList } from './components/QuestionBlocksList';
import {
  processReviewResponse,
  markObjectiveAnswers,
  markDecisionTreeAnswers,
  markContextAnswers,
  markCaseStudyAnswers,
} from './utils/reviewProcessor';
import {
  getEntryKey,
  buildStepKey,
  buildPositionKey,
  findEntryByKey,
  findEntryIndexByKey,
  getEntryReviewMatches,
  countEntryAnsweredSteps,
  filterQuestionsForReview,
} from './utils/questionIdentity';
import { InstructionsDifQuestions } from './components/InstructionsDifQuestions';
import { NotesDrawer, NotesButton, clearSimuladoNotes } from './components/NotesDrawer';
import { getQuestionBlocksPreference } from 'utils/questionBlocksPreference';

const EMPTY_STATE = {
  selectedClass: {} as Record<string, unknown>,
  course: {} as Record<string, unknown>,
};
const LECTURE_LOADING_TIMEOUT_MS = 15_000;

/**
 * Posição de cada item da prova, indexada pela chave do item.
 *
 * Por item e não por `id_questao`: um Estudo de Caso com etapas posicionadas
 * individualmente ocupa várias posições, todas com o mesmo `id_questao`, e um
 * mapa por questão guardaria só a última.
 */
export const buildQuestionOrderMap = (questions: LectureQuestion[]) =>
  questions.reduce<Record<string, number>>((acc, question, index) => {
    acc[getEntryKey(question)] = question.pivot?.ordem ?? index + 1;
    return acc;
  }, {});

// Numeração original (expandida) de cada questão/step na prova completa, chaveada por
// `getEntryKey` (questões tradicionais) ou `buildStepKey` (cada step de um Estudo de Caso).
// Cada step conta como uma questão individual. Usada para preservar a numeração original das
// questões mesmo quando um filtro de revisão (corretas/erradas) está ativo.
export const buildExpandedOriginalNumbers = (
  questions: LectureQuestion[],
): Record<string, number> => {
  const map: Record<string, number> = {};
  let counter = 0;
  for (const q of questions) {
    if (q.question_type === 'case_study') {
      for (const step of q.step_multiple ?? []) {
        counter++;
        map[buildStepKey(q, step.id)] = counter;
      }
    } else {
      counter++;
      map[getEntryKey(q)] = counter;
    }
  }
  return map;
};

const COMMON_INSTRUCTIONS_QUESTION_TYPES = ['objective_questions', 'case_study'];

export const shouldShowMixedInstructions = (questions: LectureQuestion[] = []) =>
  questions.some((q) => !COMMON_INSTRUCTIONS_QUESTION_TYPES.includes(q.question_type));

// Exportado sem o `connect` para os testes de integração da página (o default segue conectado)
export function Lectures(props: LecturesComponentType) {
  const screenWidth = window.innerWidth;
  const [lectureStarted, setLectureStarted] = useState(false);
  const [lecture, setLecture] = useState<LectureData | null>(null);
  const [isMoreThanOneTypeQuestions, setIsMoreThanOneTypeQuestions] = useState(false);
  const [questionSelected, setQuestionSelected] = useState<LectureQuestion | null>(null);
  const [caseStudyStepIndex, setCaseStudyStepIndex] = useState(0);
  const [time, setTime] = useState(0);
  const [answers, setAnswers] = useState<LectureAnswersState>({
    objective: {},
    context: {},
    decisionTree: {},
    caseStudy: {},
    questionTime: {},
    contador: '00:00:00',
  });
  const [widthComponentQuestionsSelectArea, setWidthComponentQuestionsSelectArea] = useState({
    scrollWidth: 0,
    clientWidth: 0,
    scrollLeft: 0,
  });
  const [reviewLecture, setReviewLecture] = useState({
    isReview: false,
    testId: '',
  });
  const [reviewFilter, setReviewFilter] = useState<'all' | 'correct' | 'wrong'>('all');
  const [testReviewTime, setTestReviewTime] = useState('');
  const [modalInformation, setModalInformation] = useState<ModalInformationProps>({
    open: false,
    icon: <></>,
    title: '',
  });
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [questionsReview, setQuestionsReview] = useState<ReviewQuestion[]>([]);
  // Sinaliza que o payload da revisão já chegou (mesmo vazio). Distingue "carregando"
  // de "prova sem nenhuma resposta", que também produz `questionsReview` vazio.
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [blockScreen, setBlockScreen] = useState<'list' | 'active'>('list');
  const [activeBlockId, setActiveBlockId] = useState<number | null>(null);
  // Sinal de navegação: distingue "veio de um bloco" ou "veio de responder a prova inteira" (ambos
  // devem voltar para a lista de blocos ao sair) de "nunca saiu da lista"/"revisão aberta direto do
  // curso" (devem voltar para o curso). activeBlockId não basta: ele é null tanto na lista quanto no
  // fluxo de prova inteira, que são casos opostos para o alvo de saída.
  const [blockOrigin, setBlockOrigin] = useState<BlockOrigin>(null);
  const [loadingBlockId, setLoadingBlockId] = useState<number | null>(null);
  const [loadingWholeLecture, setLoadingWholeLecture] = useState(false);

  const SIMULATED_WITH_VIDEOS = lecture?.class_type?.id === 12;

  const {
    location: { state },
    match: { params },
    getLectureDetails,
    isLoading,
    history,
  } = props as any;

  const { selectedClass, course } = state || EMPTY_STATE;

  const isSimulado =
    lecture?.class_type?.slug === 'simulado' || lecture?.class_type?.slug === 'simuladocomvideo';

  // Preferência do aluno (checkbox na aba "Fazer Prova", ver DesktopLecture.component.js),
  // lembrada por aula no localStorage. `isQuestionBlocksMode` diz que a aula TEM blocos
  // configurados; esta flag decide se o aluno quer vê-los divididos ou a prova completa.
  const blocksPreferenceEnabled = getQuestionBlocksPreference(selectedClass?.id);
  const blocksMode = isQuestionBlocksMode(lecture) && blocksPreferenceEnabled;
  const activeBlock = useMemo(
    () => lecture?.question_blocks?.find((b) => b.id === activeBlockId) ?? null,
    [lecture, activeBlockId],
  );

  // O drawer de anotações (position: fixed + z-index próprio) pintaria por cima do overlay do
  // ModalInformation (react-modal sem z-index no overlay); fechamos as anotações sempre que um
  // modal abre para não bloquear as ações obrigatórias (finalizar/sair da prova).
  useEffect(() => {
    if (modalInformation.open) setNotesOpen(false);
  }, [modalInformation.open]);

  const { debouncedPersist, persistImmediately } = usePersistLectureAnswers({
    courseTag: course?.tag || '',
    lessonId: selectedClass?.id || 0,
    lecture: lecture,
  });

  useRestoreLectureAnswers({
    lecture: lecture,
    setAnswers,
    setTime,
  });

  useEffect(() => {
    if (reviewLecture.isReview) return;

    const timer = setInterval(() => {
      if (!isTimerRunning) return;
      setTime((prevTime) => prevTime + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimerRunning, isSimulado, reviewLecture.isReview]);

  // Chave da entrada selecionada, espelhada em ref para ser lida dentro de timeouts
  const selectedEntryKeyRef = useRef<string | null>(null);

  useEffect(() => {
    selectedEntryKeyRef.current = questionSelected ? getEntryKey(questionSelected) : null;
  }, [questionSelected]);

  // Respostas espelhadas em ref: o auto-avanço do Estudo de Caso persiste de dentro de um
  // setTimeout, onde o `answers` da closure é o do render que AGENDOU o timer — anterior,
  // portanto, à resposta que acabou de ser dada. Persistir aquele snapshot cancelava o
  // debounce (persistImmediately faz clearTimeout) e não enviava nada: a etapa respondida
  // ficava só no estado do React e voltava em branco na retomada da prova.
  const answersRef = useRef(answers);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // A entrada selecionada pode ter menos steps que a anterior (ex.: sair de um vínculo legado
  // de N etapas para uma entrada de etapa única). Sem o clamp, `step_multiple[index]` fica
  // indefinido e o CaseStudyQuestion renderiza null — tela em branco.
  useEffect(() => {
    const totalSteps =
      questionSelected?.question_type === 'case_study'
        ? (questionSelected.step_multiple?.length ?? 1)
        : 1;
    setCaseStudyStepIndex((prev) => Math.min(prev, Math.max(totalSteps - 1, 0)));
  }, [questionSelected]);

  const reviewLoadingRef = useRef(false);
  const lectureLoadingRef = useRef(false);
  const failsafeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedClassId = selectedClass?.id;
  const testId = params?.testId;

  useEffect(() => {
    if (testId) {
      if (reviewLoadingRef.current) return;
      reviewLoadingRef.current = true;
      setLoadingScreen(true);

      setReviewLecture({ isReview: true, testId });

      getLectureWrongQuestions(testId)
        .then((reviewData: ReviewResponse) => {
          const questionsGrouped = reviewData.questions.reduce(
            (acc, question) => {
              if (question.question_type === 'decision_tree') {
                const allStepsForQuestion = reviewData.questions.filter(
                  (q) =>
                    q.question_type === 'decision_tree' && q.question_id === question.question_id,
                );

                const hasAnyWrongStep = allStepsForQuestion.some((q) => q.is_correct === false);

                const maxQuestionStepId = Math.max(
                  ...allStepsForQuestion.map((q) => q.question_step_id || 0),
                );

                acc[String(question.question_id)] = {
                  ...question,
                  question_step_id: maxQuestionStepId,
                  is_correct: !hasAnyWrongStep,
                };
              } else if (question.question_type === 'case_study') {
                // Cada step do case_study é uma questão independente: usa chave composta
                // para não sobrescrever o step anterior com o posterior
                const key = `${question.question_id}-${question.question_step_id}`;
                acc[key] = question;
              } else {
                acc[String(question.question_id)] = question;
              }
              return acc;
            },
            {} as Record<string, ReviewQuestion>,
          );

          setQuestionsReview(Object.values(questionsGrouped));
          setReviewLoaded(true);
          getLectureDetails(reviewData.exam_info.lesson_id, (apiLecture: LectureData) => {
            const scopedLecture: LectureData = {
              ...apiLecture,
              questions: scopeQuestionsToReview(apiLecture.questions, reviewData),
            };

            const { answers: processedAnswers, totalTime } = processReviewResponse(
              reviewData,
              scopedLecture,
            );

            let markedLecture = markObjectiveAnswers(scopedLecture, reviewData);
            markedLecture = markDecisionTreeAnswers(markedLecture, reviewData);
            markedLecture = markContextAnswers(markedLecture, reviewData);
            markedLecture = markCaseStudyAnswers(markedLecture, reviewData);

            setLecture(markedLecture);
            setAnswers(processedAnswers);
            setTime(totalTime);
            setTestReviewTime(reviewData.exam_info.time_spent);

            if (markedLecture.questions && markedLecture.questions.length > 0) {
              setQuestionSelected(markedLecture.questions[0]);
            }

            // Aulas com blocos habilitados iniciam com blockScreen 'list' — sem isso, a revisão
            // aberta direto da tela de curso (fora do fluxo de blocos) cairia na lista de blocos
            // em vez de mostrar a prova revisada.
            setBlockScreen('active');
            setLectureStarted(true);
            setLoadingScreen(false);
            reviewLoadingRef.current = false;
          });
        })
        .catch((error) => {
          setModalInformation({
            open: true,
            icon: <img src={infomationIcon} alt="Erro" className="Error" />,
            iconPosition: 'center',
            title: 'Erro ao carregar revisão da prova. Por favor, tente novamente.',
            primaryButtonText: 'Voltar',
            primaryButtonAction: handleBackToLesson,
            onclose: handleBackToLesson,
          });
          setLoadingScreen(false);
          reviewLoadingRef.current = false;
        });
    } else if (selectedClassId) {
      if (lectureLoadingRef.current) return;
      lectureLoadingRef.current = true;
      setLoadingScreen(true);

      failsafeTimeoutRef.current = setTimeout(() => {
        if (lectureLoadingRef.current) {
          setLoadingScreen(false);
          lectureLoadingRef.current = false;
        }
      }, LECTURE_LOADING_TIMEOUT_MS);

      getLectureDetails(selectedClassId, (apiLecture: LectureData) => {
        if (failsafeTimeoutRef.current) clearTimeout(failsafeTimeoutRef.current);

        if (isQuestionBlocksMode(apiLecture) && blocksPreferenceEnabled) {
          setLecture({ ...apiLecture, questions: [] });
          setBlockScreen('list');
          setActiveBlockId(null);
          setQuestionSelected(null);
          setLoadingScreen(false);
          lectureLoadingRef.current = false;
          return;
        }

        setLecture(apiLecture);
        setIsMoreThanOneTypeQuestions(shouldShowMixedInstructions(apiLecture.questions ?? []));
        if (apiLecture.questions && apiLecture.questions.length > 0) {
          setQuestionSelected(apiLecture.questions[0]);
        }

        setLoadingScreen(false);
        lectureLoadingRef.current = false;
      });
    }
    //eslint-disable-next-line
  }, [selectedClass, getLectureDetails, params?.testId]);

  const handleStartLecture = () => {
    setLectureStarted(true);
    setTime(0);
  };

  // Recebe a chave da ENTRADA (getEntryKey), não o id_questao: o mesmo Estudo de Caso pode
  // ocupar várias posições da prova, cada uma com sua etapa.
  const handleSelectQuestion = (entryKey: string) => {
    const question = findEntryByKey(lecture?.questions, entryKey);
    if (question) {
      if (questionSelected && lectureStarted && !reviewLecture.isReview && !isSimulado) {
        persistImmediately(answersRef.current, questionSelected);
      }
      setCaseStudyStepIndex(0);
      setQuestionSelected(question);
    }
  };

  // Navega diretamente para um step específico de uma questão Estudo de Caso
  const handleSelectCaseStudyStep = (entryKey: string, stepIndex: number) => {
    const question = findEntryByKey(lecture?.questions, entryKey);
    if (question) {
      if (questionSelected && lectureStarted && !reviewLecture.isReview && !isSimulado) {
        persistImmediately(answersRef.current, questionSelected);
      }
      const totalSteps =
        question.question_type === 'case_study' ? (question.step_multiple?.length ?? 1) : 1;
      setCaseStudyStepIndex(Math.min(Math.max(stepIndex, 0), Math.max(totalSteps - 1, 0)));
      setQuestionSelected(question);
    }
  };

  // O listener de scroll do container mantém widthComponentQuestionsSelectArea sincronizado,
  // então os handlers só precisam disparar a rolagem
  const handleViewNextsQuestions = () => {
    const el = document.getElementById(QUESTIONS_SELECT_AREA_ID);
    el?.scrollTo({ left: el.scrollLeft + QUESTIONS_SELECT_SCROLL_STEP, behavior: 'smooth' });
  };

  const handleViewPreviewQuestions = () => {
    const el = document.getElementById(QUESTIONS_SELECT_AREA_ID);
    el?.scrollTo({ left: el.scrollLeft - QUESTIONS_SELECT_SCROLL_STEP, behavior: 'smooth' });
  };

  const handleCloseModalInformations = () => {
    setModalInformation({ open: false, icon: <></>, title: '' });
  };

  const handleBackToLesson = () => {
    history.push(`/courses/${params?.courseId}/${params?.courseClassId}/${selectedClass?.id}`);
  };

  const refreshQuestionBlocks = async () => {
    if (!selectedClassId) return;
    try {
      const refreshed: LectureData | undefined = await fetchLectureDetailsApi(selectedClassId, {
        includeQuestions: false,
      });
      if (refreshed?.question_blocks) {
        setLecture((prev) =>
          prev ? { ...prev, question_blocks: refreshed.question_blocks } : prev,
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar status dos blocos:', error);
    }
  };

  const handleBackToBlocksList = () => {
    setBlockScreen('list');
    setActiveBlockId(null);
    setBlockOrigin(null);
    setQuestionSelected(null);
    setReviewLecture({ isReview: false, testId: '' });
    setReviewFilter('all');
    setQuestionsReview([]);
    setTestReviewTime('');
    setLecture((prev) => (prev ? { ...prev, questions: [] } : prev));
    refreshQuestionBlocks();
  };

  const handleSelectWholeLecture = async () => {
    if (!selectedClassId || loadingBlockId || loadingWholeLecture) return;
    setLoadingWholeLecture(true);
    try {
      // Busca o array plano completo sempre fresco (nunca reaproveita um snapshot antigo): um
      // bloco pode ter sido concluído nesta mesma sessão, e "prova inteira" precisa refletir isso.
      const fresh: LectureData | undefined = await fetchLectureDetailsApi(selectedClassId);
      if (!fresh?.questions) {
        throw new Error('Resposta inválida ao buscar as questões da prova inteira');
      }
      setReviewLecture({ isReview: false, testId: '' });
      setLecture((prev) => (prev ? { ...prev, questions: fresh.questions } : prev));
      setIsMoreThanOneTypeQuestions(shouldShowMixedInstructions(fresh.questions));
      setCaseStudyStepIndex(0);
      setQuestionSelected(fresh.questions[0] ?? null);
      setTime(0);
      setActiveBlockId(null);
      setBlockOrigin('whole');
      setBlockScreen('active');
      setLectureStarted(false);
    } catch (error) {
      console.error('Erro ao carregar a prova inteira:', error);
      setModalInformation({
        open: true,
        icon: <img src={infomationIcon} alt="Erro" className="Error" />,
        iconPosition: 'center',
        title: 'Erro ao carregar a prova inteira. Por favor, tente novamente.',
        primaryButtonText: 'Fechar',
        primaryButtonAction: handleCloseModalInformations,
      });
    } finally {
      setLoadingWholeLecture(false);
    }
  };

  const handleSelectBlock = async (block: QuestionBlockSummary) => {
    if (!selectedClassId || loadingBlockId || loadingWholeLecture) return;
    setLoadingBlockId(block.id);
    setReviewLecture({ isReview: false, testId: '' });
    try {
      const response: LectureBlockQuestionsResponse | undefined = await getLectureBlockQuestions(
        selectedClassId,
        block.id,
      );
      if (!response?.questions) {
        throw new Error('Resposta inválida ao buscar as questões do bloco');
      }
      setLecture((prev) => (prev ? { ...prev, questions: response.questions } : prev));
      setIsMoreThanOneTypeQuestions(shouldShowMixedInstructions(response.questions));
      setCaseStudyStepIndex(0);
      setQuestionSelected(response.questions[0] ?? null);
      setTime(0);
      setActiveBlockId(block.id);
      setBlockOrigin('block');
      setBlockScreen('active');
      setLectureStarted(true);
    } catch (error) {
      console.error('Erro ao carregar as questões do bloco:', error);
      setModalInformation({
        open: true,
        icon: <img src={infomationIcon} alt="Erro" className="Error" />,
        iconPosition: 'center',
        title: 'Erro ao carregar as questões deste bloco. Por favor, tente novamente.',
        primaryButtonText: 'Fechar',
        primaryButtonAction: handleCloseModalInformations,
      });
    } finally {
      setLoadingBlockId(null);
    }
  };

  const handleExitTarget = () => {
    if (shouldExitToBlocksList(blocksMode, blockOrigin)) {
      handleBackToBlocksList();
    } else {
      handleBackToLesson();
    }
  };

  const handleRequestOutLecture = () => {
    if (!lectureStarted || reviewLecture.isReview) {
      handleExitTarget();
    } else {
      setModalInformation({
        open: true,
        icon: <img src={infomationIcon} alt="Alerta" className="Alerta" />,
        iconPosition: 'left',
        title: 'Você tem certeza que gostaria de sair da prova?',
        primaryButtonText: 'Sim, quero sair',
        primaryButtonAction: () => {
          handleCloseModalInformations();
          handleExitTarget();
        },
        secondaryButtonText: 'Não, quero ficar',
        secondaryButtonAction: handleCloseModalInformations,
        onclose: handleCloseModalInformations,
      });
    }
  };

  const handleAnswerChange = (questionId: number, answer: any) => {
    // A resposta vem sempre da entrada renderizada, então ela é a fonte correta — buscar por
    // `id_questao` acharia a primeira entrada do Estudo de Caso, não necessariamente esta.
    const question =
      questionSelected && questionSelected.id_questao === questionId
        ? questionSelected
        : lecture?.questions?.find((q) => q.id_questao === questionId);
    if (!question) return;

    setAnswers((prev) => {
      const newAnswers = { ...prev };

      newAnswers.questionTime[questionId] = formatTime(time);
      newAnswers.contador = formatTime(time);

      if (question.question_type === 'objective_questions') {
        newAnswers.objective[questionId] = answer;
        handleSelectNextQuestion();
      } else if (question.question_type === 'question_context') {
        newAnswers.context[questionId] = answer;
      } else if (question.question_type === 'decision_tree') {
        newAnswers.decisionTree[questionId] = answer;
      } else if (question.question_type === 'case_study') {
        const { stepId, answerId } = answer as { stepId: number; answerId: number };
        newAnswers.caseStudy = {
          ...newAnswers.caseStudy,
          [questionId]: {
            ...(newAnswers.caseStudy[questionId] || {}),
            [stepId]: answerId,
          },
        };
        // Não avança aqui — o setTimeout abaixo dá tempo para o usuário ver a seleção azul
      }

      if (lectureStarted && !reviewLecture.isReview && !isSimulado) {
        debouncedPersist(newAnswers, question);
      }

      return newAnswers;
    });

    // Auto-avança o case_study APÓS um breve delay para o campo selecionado ficar azul
    // (separar do setAnswers evita que React batelha os dois updates em um único render)
    if (question.question_type === 'case_study' && !reviewLecture.isReview) {
      const totalSteps = question.step_multiple.length;
      const entryKeyAtAnswer = getEntryKey(question);
      setTimeout(() => {
        // Se o aluno trocou de posição dentro da janela de 400ms, avançar daqui causaria um
        // pulo duplo a partir de uma seleção obsoleta
        if (selectedEntryKeyRef.current !== entryKeyAtAnswer) return;
        if (caseStudyStepIndex < totalSteps - 1) {
          setCaseStudyStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
        } else {
          handleSelectNextQuestion();
        }
      }, 400);
    }
  };

  const verifyIfAllQuestionsAnswered = () => {
    if (!lecture) return false;

    const allQuestionsAnswered = lecture.questions.every((question) =>
      isQuestionAnswered(question, answers),
    );

    if (!allQuestionsAnswered) {
      setModalInformation({
        open: true,
        icon: <img src={infomationIcon} alt="Alerta" className="Alerta" />,
        iconPosition: 'left',
        title: 'Você precisa responder todas as questões antes de finalizar a prova!',
        primaryButtonText: 'Ver questões não respondidas',
        primaryButtonAction: handleGetFirstQuestionUnanswered,
        secondaryButtonText: 'Cancelar',
        secondaryButtonAction: handleCloseModalInformations,
        onclose: handleCloseModalInformations,
      });
    }

    return allQuestionsAnswered;
  };

  const handleGetFirstQuestionUnanswered = () => {
    if (!lecture) return;

    const firstQuestionUnanswered = lecture.questions.find(
      (question) => !isQuestionAnswered(question, answers),
    );

    if (firstQuestionUnanswered) {
      setCaseStudyStepIndex(0);
      setQuestionSelected(firstQuestionUnanswered);
    }

    handleCloseModalInformations();
  };

  const filteredQuestions = useMemo(() => {
    if (!lecture || !reviewLecture.isReview || reviewFilter === 'all') {
      return lecture?.questions || [];
    }

    return filterQuestionsForReview(lecture.questions, questionsReview ?? [], reviewFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lecture,
    reviewLecture.isReview,
    reviewFilter,
    answers.objective,
    answers.decisionTree,
    questionsReview,
  ]);

  // Mede a área de chips de questões depois que ela existe no DOM (após o painel renderizar)
  // e mantém as medidas sincronizadas com rolagem, redimensionamento da janela e mudanças
  // na lista de questões exibidas (ex.: troca de filtro na revisão)
  useEffect(() => {
    const el = document.getElementById(QUESTIONS_SELECT_AREA_ID);
    if (!el) return;

    // Agrupa por quadro (rAF) e só atualiza o estado quando alguma medida realmente mudou.
    // Isso evita re-render da tela inteira e leituras de layout a ~60Hz durante a rolagem.
    let rafId = 0;
    const measure = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        setWidthComponentQuestionsSelectArea((prev) =>
          prev.scrollWidth === el.scrollWidth &&
          prev.clientWidth === el.clientWidth &&
          prev.scrollLeft === el.scrollLeft
            ? prev
            : {
                scrollWidth: el.scrollWidth,
                clientWidth: el.clientWidth,
                scrollLeft: el.scrollLeft,
              },
        );
      });
    };

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [lectureStarted, loadingScreen, filteredQuestions]);

  // Ao trocar o filtro na revisão, a questão que estava selecionada pode não pertencer
  // mais à lista filtrada (ex.: estava numa questão errada e mudou para "Somente corretas").
  // Nesse caso reposiciona a seleção para a primeira questão da lista filtrada — ou nenhuma,
  // quando o filtro não retorna questões. Se a questão atual continua na lista, mantém a seleção.
  useEffect(() => {
    if (!reviewLecture.isReview) return;

    const selectedEntryKey = questionSelected ? getEntryKey(questionSelected) : null;
    const isSelectedVisible =
      selectedEntryKey !== null && filteredQuestions.some((q) => getEntryKey(q) === selectedEntryKey);

    if (!isSelectedVisible) {
      setCaseStudyStepIndex(0);
      setQuestionSelected(filteredQuestions.length > 0 ? filteredQuestions[0] : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFilter, filteredQuestions, reviewLecture.isReview]);

  const questionOrderMap = useMemo(() => {
    if (!lecture?.questions) return {} as Record<string, number>;

    return buildQuestionOrderMap(lecture.questions);
    // eslint-disable-next-line
  }, [lecture?.questions]);

  const handleFinishLecture = async () => {
    if (isSubmitting) return;
    if (!verifyIfAllQuestionsAnswered() || !lecture || !course?.tag) return;

    setIsSubmitting(true);
    try {
      const questions: any[] = [];

      Object.entries(answers.objective).forEach(([questionId, answerId]) => {
        questions.push({
          question_type: 'objective_questions',
          time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
          question_id: Number(questionId),
          answer_id: answerId,
        });
      });

      Object.entries(answers.context).forEach(([questionId, contextAnswer]) => {
        questions.push({
          question_type: 'question_context',
          time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
          question_id: Number(questionId),
          context_answers: contextAnswer,
        });
      });

      Object.entries(answers.decisionTree).forEach(([questionId, userStepAnswers]) => {
        const question = lecture.questions.find((q) => q.id_questao === Number(questionId));

        if (question && question.question_type === 'decision_tree') {
          const tree = userStepAnswers.map((stepAnswer: any) => {
            let stepId = 0;
            if (stepAnswer.stepType === 'multiple' && question.step_multiple) {
              const step = question.step_multiple[stepAnswer.stepIndex];
              stepId = step?.id || 0;
            } else if (stepAnswer.stepType === 'single' && question.step_single) {
              const step = question.step_single[stepAnswer.stepIndex];
              stepId = step?.id || 0;
            }

            return {
              time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
              question_step_id: stepId,
              answer_id: stepAnswer.selectedAnswerId,
            };
          });

          questions.push({
            question_type: 'decision_tree',
            question_id: Number(questionId),
            tree,
          });
        }
      });

      Object.entries(answers.caseStudy).forEach(([questionId, stepAnswers]) => {
        if (!stepAnswers || Object.keys(stepAnswers).length === 0) return;

        const steps = Object.entries(stepAnswers).map(([stepId, answerId]) => ({
          time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
          question_step_id: Number(stepId),
          answer_id: answerId,
        }));

        questions.push({
          question_type: 'case_study',
          question_id: Number(questionId),
          tree: steps,
        });
      });

      const finalPayload = buildFinishLecturePayload(
        {
          time_spent: answers.contador,
          course: course.tag,
          lesson_id: selectedClass.id,
          questions,
        },
        blocksMode ? activeBlockId : undefined,
      );

      const responseFinishLecture = await finishLecture(finalPayload);

      setIsTimerRunning(false);

      sessionStorage.setItem('completedTestClassId', String(selectedClass.id));

      if (isSimulado) {
        clearSimuladoNotes(selectedClass.id);
        setNotesOpen(false);
      }

      if (blocksMode && activeBlock) {
        // Atualização otimista: reflete a conclusão do bloco imediatamente, sem esperar o
        // refetch (que pode demorar a refletir o exame recém-enviado no backend). answered_questions
        // volta a 0 pois não há mais rascunho ativo assim que a prova é finalizada (mesmo padrão
        // que o backend retorna) — getDisplayedAnsweredQuestions cuida de exibir o total nesse caso.
        const finishedBlockId = activeBlock.id;
        setLecture((prev) => {
          if (!prev?.question_blocks) return prev;
          return {
            ...prev,
            question_blocks: prev.question_blocks.map((b) =>
              b.id === finishedBlockId
                ? {
                    ...b,
                    status: 'completed',
                    answered_questions: 0,
                    exams_count: b.exams_count + 1,
                  }
                : b,
            ),
          };
        });
      }

      // Não chamamos refreshQuestionBlocks() aqui: handleBackToBlocksList já refaz esse fetch ao
      // voltar para a lista, e chamá-lo também neste ponto arrisca sobrescrever a atualização
      // otimista acima com uma leitura do backend ainda sem o exame recém-enviado processado.

      setModalInformation({
        icon: (
          <img src={trophyIcon} alt="Prova finalizada" className="Success" width={46} height={46} />
        ),
        iconPosition: 'center',
        open: true,
        title: `Prova finalizada com sucesso! Você acertou ${
          responseFinishLecture?.correct_answers || 0
        } de ${responseFinishLecture?.total_questions || 0} questões${
          isSimulado ? ` em ${responseFinishLecture?.time_spent || 0}` : ''
        }!`,
        primaryButtonText: 'Ver a prova corrigida >',
        primaryButtonAction: () => {
          handleCloseModalInformations();

          if (!responseFinishLecture?.test_id) return;

          setLoadingScreen(true);

          history.push(
            `/lectures/${params?.courseId}/${params?.courseClassId}/${responseFinishLecture?.test_id}`,
            {
              selectedClass,
              course,
              idAula: selectedClass.id,
            },
          );
        },
        ...(blocksMode
          ? {
              secondaryButtonText: 'Voltar para blocos',
              secondaryButtonAction: () => {
                handleCloseModalInformations();
                handleBackToBlocksList();
              },
              onclose: () => {
                handleCloseModalInformations();
                handleBackToBlocksList();
              },
            }
          : {}),
      });
    } catch (error) {
      setModalInformation({
        icon: <img src={infomationIcon} alt="Erro" className="Error" width={46} height={46} />,
        iconPosition: 'center',
        open: true,
        title: 'Erro ao finalizar a prova. Por favor, tente novamente.',
        primaryButtonText: 'Fechar',
        primaryButtonAction: handleCloseModalInformations,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectNextQuestion = () => {
    if (!lecture || !questionSelected) return;

    const questionsToUse = reviewLecture.isReview ? filteredQuestions : lecture.questions;
    const currentIndex = findEntryIndexByKey(questionsToUse, getEntryKey(questionSelected));
    if (currentIndex === -1) return;

    if (currentIndex < questionsToUse.length - 1) {
      if (lectureStarted && !reviewLecture.isReview && !isSimulado) {
        persistImmediately(answersRef.current, questionSelected);
      }
      setCaseStudyStepIndex(0);
      setQuestionSelected(questionsToUse[currentIndex + 1]);
    }
  };

  const handleSelectPreviewQuestion = () => {
    if (!lecture || !questionSelected) return;

    const questionsToUse = reviewLecture.isReview ? filteredQuestions : lecture.questions;
    const currentIndex = findEntryIndexByKey(questionsToUse, getEntryKey(questionSelected));
    if (currentIndex === -1) return;

    if (currentIndex > 0) {
      if (lectureStarted && !reviewLecture.isReview && !isSimulado) {
        persistImmediately(answersRef.current, questionSelected);
      }
      const prevQuestion = questionsToUse[currentIndex - 1];
      // Ao voltar para um Estudo de Caso, começar pelo último step
      if (prevQuestion.question_type === 'case_study') {
        setCaseStudyStepIndex(prevQuestion.step_multiple.length - 1);
      } else {
        setCaseStudyStepIndex(0);
      }
      setQuestionSelected(prevQuestion);
    }
  };

  // Navega para o próximo step de uma questão Estudo de Caso
  const handleCaseStudyNextStep = () => {
    if (!questionSelected || questionSelected.question_type !== 'case_study') return;
    const total = questionSelected.step_multiple.length;
    setCaseStudyStepIndex((prev) => Math.min(prev + 1, total - 1));
  };

  // Navega para o step anterior de uma questão Estudo de Caso
  const handleCaseStudyPrevStep = () => {
    setCaseStudyStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleChangeItemReview = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedItem = itemsSelectOnReview.find((item) => item.title === e.target.value);
    if (selectedItem) {
      setReviewFilter(selectedItem.value as 'all' | 'correct' | 'wrong');
    }
  };

  const currentQuestionNumber = questionSelected
    ? (questionOrderMap[getEntryKey(questionSelected)] ?? questionSelected.pivot?.ordem ?? 1)
    : 1;

  // Total expandido: cada step do Estudo de Caso conta como uma questão individual
  // Memoizado para não recriar o array a cada render e não invalidar os useMemo dependentes
  // Sempre calculado sobre a prova completa: o filtro da revisão não deve alterar o total
  const lectureQuestions = useMemo(() => lecture?.questions ?? [], [lecture]);
  const expandedTotalQuestions = useMemo(() => {
    return lectureQuestions.reduce((acc, q) => {
      if (q.question_type === 'case_study') return acc + q.step_multiple.length;
      return acc + 1;
    }, 0);
  }, [lectureQuestions]);

  // Numeração original das questões/steps na prova completa (ver buildExpandedOriginalNumbers).
  // Mantida sobre a prova inteira para preservar a numeração mesmo com filtro de revisão ativo.
  const expandedOriginalNumbers = useMemo(
    () => buildExpandedOriginalNumbers(lectureQuestions),
    [lectureQuestions],
  );

  // Posição sequencial atual contando steps do Estudo de Caso como questões individuais.
  // Baseada na prova completa para manter a numeração original independentemente do filtro.
  const expandedQuestionNumber = useMemo(() => {
    if (!questionSelected) return currentQuestionNumber;
    return (
      expandedOriginalNumbers[buildPositionKey(questionSelected, caseStudyStepIndex)] ??
      currentQuestionNumber
    );
  }, [expandedOriginalNumbers, questionSelected, caseStudyStepIndex, currentQuestionNumber]);

  // Contagem expandida: cada step respondido do Estudo de Caso conta individualmente
  // Sempre sobre a prova completa, para manter consistência com o total exibido
  const expandedAnsweredCount = useMemo(() => {
    let count = 0;
    for (const q of lectureQuestions) {
      if (q.question_type === 'case_study') {
        // Conta apenas os steps DESTA entrada: o balde answers.caseStudy[id] é compartilhado
        // por todas as entradas do mesmo Estudo de Caso e seria somado N vezes
        count += countEntryAnsweredSteps(q, answers);
      } else if (isQuestionAnswered(q, answers)) {
        count++;
      }
    }
    return count;
  }, [lectureQuestions, answers]);

  const countCorrectQuestions = () => {
    if (!lecture || !reviewLecture.isReview) {
      return { correctQuestions: 0, totalQuestions: 0 };
    }

    // Só contam as revisões que ainda correspondem a uma posição da prova: um step
    // desvinculado da aula depois da entrega continua no payload e faria o acerto passar do total
    const attachedReviews = lecture.questions.flatMap((q) =>
      getEntryReviewMatches(q, questionsReview ?? []),
    );

    return {
      correctQuestions: attachedReviews.filter((q) => q.is_correct).length,
      // expandedTotalQuestions conta cada step do case_study individualmente,
      // alinhado com os chips exibidos no cabeçalho
      totalQuestions: expandedTotalQuestions,
    };
  };

  if (isLoading || !lecture || loadingScreen) {
    return (
      <MainLayout title={lecture?.titulo || 'Carregando...'} className="home lecture-page">
        <Container>
          <Loading courseTag={(course?.tag as string) || ''} />
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={lecture?.titulo} className="home lecture-page">
      <Container>
        <Header>
          {screenWidth <= 768 ? (
            <IconButton onClick={handleRequestOutLecture}>
              <img src={arrowPrev} alt="sair" />
            </IconButton>
          ) : (
            <Button width="110px" variant="invisible" onClick={handleRequestOutLecture}>
              <img src={times} alt="Sair" />
              Sair
            </Button>
          )}

          <Title margin={!lectureStarted || reviewLecture.isReview ? '0px 110px 0px 0px' : '0px'}>
            {reviewLecture.isReview
              ? `Revisão ${lecture?.titulo} - ${course?.title}`
              : !lectureStarted || !isSimulado
                ? `${lecture?.titulo}${
                    blocksMode && blockScreen === 'active' && activeBlock
                      ? ` - ${activeBlock.title}`
                      : ''
                  } - ${course?.title}`
                : formatTime(time)}
          </Title>

          {lectureStarted &&
            !reviewLecture.isReview &&
            isSimulado &&
            (!blocksMode || blockScreen === 'active') && (
            <HeaderActions>
              <NotesButton onClick={() => setNotesOpen(true)} />
              <Button
                variant={isTimerRunning ? 'danger' : 'success'}
                onClick={() => setIsTimerRunning(!isTimerRunning)}
              >
                {isTimerRunning ? (
                  <>
                    <img src={stopWhite} alt="Parar" />
                    Pausar
                  </>
                ) : (
                  <>
                    <img src={playWhite} alt="Continuar" />
                    Continuar
                  </>
                )}
              </Button>
            </HeaderActions>
          )}
        </Header>

        {blocksMode && blockScreen === 'list' ? (
          <QuestionBlocksList
            blocks={lecture.question_blocks ?? []}
            loadingBlockId={loadingBlockId}
            onSelectBlock={handleSelectBlock}
            onSelectWholeLecture={handleSelectWholeLecture}
            loadingWholeLecture={loadingWholeLecture}
          />
        ) : !lectureStarted ? (
          isMoreThanOneTypeQuestions ? (
            <InstructionsDifQuestions
              handleStartLecture={handleStartLecture}
              lecture={lecture as any}
            />
          ) : (
            <Instructions handleStartLecture={handleStartLecture} lecture={lecture as any} />
          )
        ) : (
          <PanelArea>
            <LecturesHeaderPanel
              currentLecture={reviewLecture.isReview ? filteredQuestions : lecture.questions}
              questionSelected={questionSelected}
              answers={answers}
              handleSelectQuestion={handleSelectQuestion}
              caseStudyStepIndex={caseStudyStepIndex}
              onSelectCaseStudyStep={handleSelectCaseStudyStep}
              handleViewNextsQuestions={handleViewNextsQuestions}
              handleViewPreviewQuestions={handleViewPreviewQuestions}
              widthComponentQuestionsSelectArea={widthComponentQuestionsSelectArea}
              isReview={reviewLecture.isReview}
              testReviewTime={testReviewTime}
              countCorrectQuestions={countCorrectQuestions}
              reviewType={itemsSelectOnReview.find((item) => item.value === reviewFilter)}
              handleChangeItemReview={handleChangeItemReview}
              itemsSelectOnReview={itemsSelectOnReview}
              questionsReview={questionsReview}
              reviewLoaded={reviewLoaded}
              isSimulated={isSimulado}
              questionOrderMap={questionOrderMap}
              expandedOriginalNumbers={expandedOriginalNumbers}
            />

            {questionSelected && (
              <QuestionRenderer
                // Remonta ao trocar de posição: duas entradas do mesmo Estudo de Caso são
                // questões diferentes e não podem reaproveitar o estado interno do componente
                key={getEntryKey(questionSelected)}
                question={questionSelected}
                questionNumber={expandedQuestionNumber}
                totalQuestions={expandedTotalQuestions}
                answeredQuestionsCount={expandedAnsweredCount}
                answers={answers}
                onAnswerChange={handleAnswerChange}
                isReview={reviewLecture.isReview}
                onNextQuestion={handleSelectNextQuestion}
                provaId={reviewLecture.testId ? Number(reviewLecture.testId) : undefined}
                caseStudyStepIndex={caseStudyStepIndex}
              />
            )}

            {questionSelected && (
              <LecturesFooterPanel
                questionSelected={questionSelected}
                currentLecture={reviewLecture.isReview ? filteredQuestions : lecture.questions}
                answers={answers}
                isReview={reviewLecture.isReview}
                handleSelectNextQuestion={handleSelectNextQuestion}
                handleSelectPreviewQuestion={handleSelectPreviewQuestion}
                handleFinishLecture={handleFinishLecture}
                isSimulated={isSimulado}
                simulatesWithVideos={SIMULATED_WITH_VIDEOS}
                isSubmitting={isSubmitting}
                caseStudyStepIndex={caseStudyStepIndex}
                onCaseStudyNextStep={handleCaseStudyNextStep}
                onCaseStudyPrevStep={handleCaseStudyPrevStep}
              />
            )}
          </PanelArea>
        )}

        {lectureStarted &&
          !reviewLecture.isReview &&
          isSimulado &&
          (!blocksMode || blockScreen === 'active') &&
          selectedClass?.id && (
            <NotesDrawer
              // Remonta a cada bloco/aula: garante que o estado local seja ressemeado do
              // sessionStorage (já limpo ao finalizar) e não vaze anotações entre blocos
              key={`notes-${selectedClass.id}-${activeBlockId ?? 'whole'}`}
              lessonId={selectedClass.id}
              open={notesOpen}
              onClose={() => setNotesOpen(false)}
            />
          )}

        <ModalInformation
          icon={modalInformation.icon}
          iconPosition={modalInformation.iconPosition}
          open={modalInformation.open}
          primaryButtonAction={modalInformation.primaryButtonAction}
          primaryButtonText={modalInformation.primaryButtonText}
          secondaryButtonAction={modalInformation.secondaryButtonAction}
          secondaryButtonText={modalInformation.secondaryButtonText}
          title={modalInformation.title}
          onclose={modalInformation.onclose}
        />
      </Container>
    </MainLayout>
  );
}

const mapStateToProps = (state: AppState) => ({
  isLoading: classSelectors.isLoading(state),
  user: authSelectors.getUser(state),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  getLectureDetails: (classId, next) => dispatch(classActions.getLectureDetails(classId, next)),
  finishLecture: (finalPayload, userId, classId, next) =>
    dispatch(classActions.finishLecture(finalPayload, userId, classId, next)),
  getCourseDetails: (courseId, courseClassId: null | undefined, next) =>
    dispatch(courseActions.getCourseDetails(courseId, courseClassId, next)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Lectures);

type DispatchProps = {
  getLectureDetails: (classId: string, next: (data: LectureData) => void) => void;
  finishLecture: (finalPayload: any, userId: string, classId: string, next: () => void) => void;
  getCourseDetails: (courseId: string, courseClassId: null | undefined, next: () => void) => void;
};

type UserProfileType = {
  email: string;
  firstName: string;
  foto: string;
  id: number;
  id_profile: number;
  lastName: string;
  name: string;
  phoneNumber: string;
  require_password_change: boolean;
};

type AppState = {
  isLoading: boolean;
  user: UserProfileType;
};

type LecturesComponentType = DispatchProps &
  AppState & {
    location: any;
    match: any;
    history: any;
  };
